import type { Awaitable } from '#layers/base/public';
import type { BlockBase } from '#layers/block-engine/types/block';
import { computed, markRaw, onBeforeUnmount, ref, shallowRef } from 'vue';
import { defineStore } from '#imports';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';

export interface BlockDebugData {
	renderId: string;
	parentRenderId: string | null;
	blockId: string;
	getVariables: () => Record<string, any>;
	getProps: () => Record<string, any>;
}

export interface BlockDebugDataWithChildren extends BlockDebugData {
	children: BlockDebugDataWithChildren[];
	getBlock: () => Awaitable<BlockBase | undefined>;
}

export const useBlockDebuggerStore = defineStore('block-debugger', () => {
	const blockResolver = useBlockLibraryStore();

	/**
	 * Additional block definitions.
	 */
	const blockDefs = shallowRef<Array<BlockBase>>([]);

	const blocks = ref<Record<string, BlockDebugData>>({});

	function registerBlock(data: BlockDebugData) {
		unregisterBlock(data.renderId);

		blocks.value[data.renderId] = data;

		function unregister() {
			unregisterBlock(data.renderId);
		}

		onBeforeUnmount(() => {
			unregister();
		});

		return {
			unregister,
		};
	}

	function unregisterBlock(renderId: string) {
		delete blocks.value[renderId];
	}

	function getBlock(renderId: string) {
		return blocks.value[renderId];
	}

	function getBlockChildren(renderId: string) {
		return Object.values(blocks.value).filter((block) => block.parentRenderId === renderId);
	}

	const blockTree = computed(() => {
		const mapByRenderId: Map<string, BlockDebugDataWithChildren> = new Map();
		const rootBlocks = Object.values(blocks.value).filter((block) => !block.parentRenderId);

		function buildTree(block: BlockDebugData): BlockDebugDataWithChildren {
			const children = getBlockChildren(block.renderId);

			const data = markRaw({
				...block,
				children: children.map((block) => buildTree(block)),
				getBlock() {
					return blockDefs.value.find((b) => b.id === block.blockId) ?? blockResolver.resolveBlock(block.blockId);
				},
			} satisfies BlockDebugDataWithChildren);

			mapByRenderId.set(block.renderId, data);
			return data;
		}

		return {
			tree: rootBlocks.map((block) => buildTree(block)),
			mapByRenderId,
		};
	});

	function getBlockTreeByBlockId(blockId: string) {
		const renderId = Object.values(blocks.value).find((block) => block.blockId === blockId)?.renderId;

		if (!renderId) {
			return [];
		}

		return [blockTree.value.mapByRenderId.get(renderId)!];
	}

	return {
		registerBlock,
		unregisterBlock,
		getBlock,
		getBlockChildren,
		getBlockTreeByBlockId,
		blockDefs,
	};
});
